"use client";

import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { RoundButtonWithIcon } from "@/components/tools/RoundButtonWithIcon";
import { useAppContext } from "@/context/AppContext";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import type {
  BankStatementImportResult,
  ImportedBankTransaction,
} from "@/types";
import { importBankStatement } from "@/utils";
import { buildAuthPayload } from "@/utils/MyEstateHelper";
import { FileText, Upload } from "lucide-react";
import React, { useRef, useState } from "react";
import { toast } from "sonner";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

type BankStatementImportProps = {
  estateId: number;
  onAdd: (transactions: ImportedBankTransaction[]) => void;
};

function readFileAsBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(
        result.includes(",") ? result.slice(result.indexOf(",") + 1) : result,
      );
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function BankStatementImport({
  estateId,
  onAdd,
}: BankStatementImportProps) {
  const { language } = useLanguage();
  const { userInfoGlobal } = useAppContext();
  const { verifyTransactionPeriods } = useEstateContext();
  const copy = {
    cs: {
      upload: "Importovat výpis CSV nebo GPC",
      title: "Import bankovního výpisu",
      description: "Vyberte transakce, které chcete přidat k této nemovitosti.",
      invalidFile: "Vyberte soubor CSV nebo GPC do velikosti 5 MB.",
      invalidResponse: "Výpis se nepodařilo načíst.",
      selectAll: "Vybrat všechny nové",
      clearAll: "Zrušit výběr",
      selected: "Vybráno: {count}",
      selectTransaction: "Vybrat transakci {name}",
      addSelected: "Předvyplnit vybrané",
      confirmImport: "Potvrdit import",
      alreadyImported: "Již importováno",
      notImported: "Nebude importováno",
      income: "Příjem",
      expense: "Výdaj",
      variableSymbol: "VS",
      noNewTransactions: "Výpis neobsahuje žádné nové transakce.",
      replacesExisting: "Nalezena aktivní transakce stejného typu",
      endPreviousRecurrence: "Ukončit předchozí opakování před {date}",
      keepPreviousRecurrence: "Ponechat předchozí opakování aktivní",
      categories: {
        RENT: "Nájem",
        WATER: "Voda",
        ELECTRICITY: "Elektřina",
        INTERNET: "Internet",
        ADVANCE_PAYMENTS: "Zálohy",
        INSURANCE: "Pojištění",
        PROPERTY_TAX: "Daň z nemovitosti",
        OTHER: "Ostatní",
      },
    },
    en: {
      upload: "Import CSV or GPC statement",
      title: "Import bank statement",
      description: "Select transactions to add to this property.",
      invalidFile: "Choose a CSV or GPC file up to 5 MB.",
      invalidResponse: "The statement could not be read.",
      selectAll: "Select all new",
      clearAll: "Clear selection",
      selected: "Selected: {count}",
      selectTransaction: "Select transaction {name}",
      addSelected: "Prefill selected",
      confirmImport: "Confirm import",
      alreadyImported: "Already imported",
      notImported: "Will not be imported",
      income: "Income",
      expense: "Expense",
      variableSymbol: "VS",
      noNewTransactions: "The statement contains no new transactions.",
      replacesExisting: "An active transaction of the same type was found",
      endPreviousRecurrence: "End the previous recurrence before {date}",
      keepPreviousRecurrence: "Keep the previous recurrence active",
      categories: {
        RENT: "Rent",
        WATER: "Water",
        ELECTRICITY: "Electricity",
        INTERNET: "Internet",
        ADVANCE_PAYMENTS: "Advance payments",
        INSURANCE: "Insurance",
        PROPERTY_TAX: "Property tax",
        OTHER: "Other",
      },
    },
  }[language];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<BankStatementImportResult | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [replacementTransactionIds, setReplacementTransactionIds] = useState<
    Record<string, number | null>
  >({});
  const [isUploading, setIsUploading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const recurringCandidatesFor = (transaction: ImportedBankTransaction) =>
    transaction.recurringTransactionCandidates?.length
      ? transaction.recurringTransactionCandidates
      : transaction.existingRecurringTransaction
        ? [transaction.existingRecurringTransaction]
        : [];
  const keepsExistingRecurrence = (transaction: ImportedBankTransaction) =>
    recurringCandidatesFor(transaction).length > 0 &&
    replacementTransactionIds[transaction.importId] == null;
  const availableTransactions =
    result?.transactions.filter(
      (transaction) =>
        !transaction.alreadyImported && !keepsExistingRecurrence(transaction),
    ) ?? [];
  const allAvailableSelected =
    availableTransactions.length > 0 &&
    availableTransactions.every((transaction) =>
      selectedIds.has(transaction.importId),
    );
  const importVerifications = Array.from(
    new Map(
      (result?.transactions ?? [])
        .filter(
          (transaction) =>
            transaction.alreadyImported &&
            transaction.existingRecurringTransaction &&
            transaction.existingRecurringTransaction.recurrence !== "ONCE",
        )
        .map((transaction) => {
          const [year, month] = transaction.date.split("-");
          const verification = {
            transactionId: transaction.existingRecurringTransaction!.id,
            period: `${month} ${year}`,
          };
          return [
            `${verification.transactionId}-${verification.period}`,
            verification,
          ] as const;
        }),
    ).values(),
  );

  const handleFile = async (file: File | undefined) => {
    if (!file || !userInfoGlobal?.token) return;
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (
      !extension ||
      !["csv", "gpc"].includes(extension) ||
      file.size > MAX_FILE_SIZE
    ) {
      toast.error(copy.invalidFile);
      return;
    }

    setIsUploading(true);
    try {
      const content = await readFileAsBase64(file);
      const payload = buildAuthPayload(
        userInfoGlobal.email,
        userInfoGlobal.token,
        {
          estateId,
          fileName: file.name,
          content,
        },
      );
      const response = await importBankStatement(payload, userInfoGlobal.token);
      if (
        response.status < 200 ||
        response.status >= 300 ||
        !response.data?.data
      ) {
        toast.error(copy.invalidResponse);
        return;
      }
      const importResult = response.data.data as BankStatementImportResult;
      setResult(importResult);
      setSelectedIds(new Set());
      setReplacementTransactionIds(
        Object.fromEntries(
          importResult.transactions.map((transaction) => [
            transaction.importId,
            transaction.existingRecurringTransaction?.id ?? null,
          ]),
        ),
      );
      setIsPreviewOpen(true);
    } catch {
      toast.error(copy.invalidResponse);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const toggleTransaction = (importId: string, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(importId);
      else next.delete(importId);
      return next;
    });
  };

  const setReplacementTransaction = (
    importId: string,
    transactionId: number | null,
  ) => {
    setReplacementTransactionIds((current) => ({
      ...current,
      [importId]: transactionId,
    }));
    toggleTransaction(importId, transactionId != null);
  };

  const toggleAll = () => {
    setSelectedIds(
      allAvailableSelected
        ? new Set()
        : new Set(
            availableTransactions.map((transaction) => transaction.importId),
          ),
    );
  };

  const addSelected = async () => {
    if (!result) return;
    const importPeriods = Array.from(
      new Set(
        result.transactions.map((transaction) => {
          const [year, month] = transaction.date.split("-");
          return `${month} ${year}`;
        }),
      ),
    );
    const selectedTransactions = result.transactions.filter(
      (transaction) =>
        !transaction.alreadyImported &&
        !keepsExistingRecurrence(transaction) &&
        selectedIds.has(transaction.importId),
    );
    const selected = selectedTransactions.map((transaction) => ({
      ...transaction,
      bankImportFileName: result.fileName,
      bankImportFormat: result.format,
      bankImportTransactionCount: selectedTransactions.length,
      bankImportPeriods: importPeriods,
      replaceRecurringTransactionId:
        replacementTransactionIds[transaction.importId] ?? null,
    }));
    if (selected.length === 0 && importVerifications.length === 0) return;
    if (
      importVerifications.length > 0 &&
      !(await verifyTransactionPeriods(
        importVerifications,
        selected.length === 0
          ? {
              fileName: result.fileName,
              format: result.format,
              transactionCount: importVerifications.length,
              periods: importPeriods,
            }
          : undefined,
      ))
    ) {
      return;
    }
    setIsPreviewOpen(false);
    setResult(null);
    setSelectedIds(new Set());
    setReplacementTransactionIds({});
    if (selected.length > 0) {
      window.setTimeout(() => onAdd(selected), 0);
    }
  };

  const handlePreviewOpenChange = (open: boolean) => {
    setIsPreviewOpen(open);
    if (!open) {
      setResult(null);
      setSelectedIds(new Set());
      setReplacementTransactionIds({});
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.gpc"
        className="sr-only"
        onChange={(event) => void handleFile(event.target.files?.[0])}
      />
      <RoundButtonWithIcon
        icon={Upload}
        text={copy.upload}
        disabled={isUploading}
        onClick={() => fileInputRef.current?.click()}
      />

      <Dialog open={isPreviewOpen} onOpenChange={handlePreviewOpenChange}>
        <DialogContent className="app-dialog-workspace sm:max-w-[760px]">
          <div className="border-b border-border/70 px-6 pb-5 pt-6">
            <DialogHeader className="pr-8">
              <DialogTitle>{copy.title}</DialogTitle>
              <DialogDescription>{copy.description}</DialogDescription>
            </DialogHeader>

            {result && (
              <div className="mt-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
                    <FileText className="size-4 shrink-0" />
                    <span className="truncate">{result.fileName}</span>
                    <span className="rounded-full bg-hover px-2 py-0.5 text-xs">
                      {result.format} · {result.transactionCount}
                    </span>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <Button
                    type="button"
                    variant="minimal"
                    disabled={availableTransactions.length === 0}
                    onClick={toggleAll}
                  >
                    {allAvailableSelected ? copy.clearAll : copy.selectAll}
                  </Button>
                  <span className="text-xs text-foreground">
                    {copy.selected.replace("{count}", String(selectedIds.size))}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-scroll px-6 py-4 [scrollbar-gutter:stable]">
            {result && (
              <div className="space-y-2">
                {result.transactions.map((transaction) => {
                  const checked = selectedIds.has(transaction.importId);
                  const isIncome = transaction.transactionType === "INCOME";
                  const recurringCandidates =
                    recurringCandidatesFor(transaction);
                  const isDisabled =
                    transaction.alreadyImported ||
                    keepsExistingRecurrence(transaction);
                  const transactionDate = new Date(
                    `${transaction.date}T12:00:00`,
                  );
                  const toggleCurrentTransaction = () => {
                    if (!isDisabled) {
                      toggleTransaction(transaction.importId, !checked);
                    }
                  };
                  return (
                    <div
                      key={transaction.importId}
                      role="checkbox"
                      aria-checked={checked}
                      aria-disabled={isDisabled}
                      tabIndex={isDisabled ? -1 : 0}
                      onClick={toggleCurrentTransaction}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          toggleCurrentTransaction();
                        }
                      }}
                      className={`flex gap-3 rounded-lg border p-3 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-softiq-primary/60 ${
                        isDisabled
                          ? "cursor-not-allowed border-border/70 opacity-60"
                          : checked
                            ? "cursor-pointer border-softiq-primary/40 bg-hover"
                            : "cursor-pointer border-border/70 hover:bg-hover"
                      }`}
                    >
                      <Checkbox
                        className="mt-0.5"
                        aria-label={copy.selectTransaction.replace(
                          "{name}",
                          transaction.suggestedName,
                        )}
                        checked={checked}
                        disabled={isDisabled}
                        onClick={(event) => event.stopPropagation()}
                        onCheckedChange={(value) =>
                          toggleTransaction(
                            transaction.importId,
                            value === true,
                          )
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex min-w-0 items-center gap-2">
                              <p className="truncate text-sm font-medium text-foreground">
                                {transaction.suggestedName}
                              </p>
                              {transaction.category !== "OTHER" ? (
                                <span className="shrink-0 rounded-full bg-hover px-2 py-0.5 text-[10px] font-medium text-foreground/70">
                                  {copy.categories[transaction.category]}
                                </span>
                              ) : null}
                            </div>
                            <p className="text-xs text-foreground">
                              {new Intl.DateTimeFormat(
                                language === "cs" ? "cs-CZ" : "en-GB",
                              ).format(transactionDate)}
                              {transaction.counterpartyAccount
                                ? ` · ${transaction.counterpartyAccount}`
                                : ""}
                              {transaction.variableSymbol
                                ? ` · ${copy.variableSymbol} ${transaction.variableSymbol}`
                                : ""}
                            </p>
                          </div>
                          <div className="text-right">
                            <p
                              className={`text-sm font-semibold ${
                                isIncome ? "text-green-600" : "text-red-600"
                              }`}
                            >
                              {isIncome ? "+" : "−"}
                              {new Intl.NumberFormat(
                                language === "cs" ? "cs-CZ" : "en-GB",
                                {
                                  style: "currency",
                                  currency: transaction.currency || "CZK",
                                },
                              ).format(transaction.amount)}
                            </p>
                            <p className="text-xs text-foreground">
                              {transaction.alreadyImported
                                ? copy.alreadyImported
                                : keepsExistingRecurrence(transaction)
                                  ? copy.notImported
                                  : isIncome
                                    ? copy.income
                                    : copy.expense}
                            </p>
                          </div>
                        </div>
                        {recurringCandidates.length > 0 &&
                        !transaction.alreadyImported ? (
                          <div
                            className="mt-2 rounded-md border border-softiq-secondary/30 bg-softiq-secondary/10 px-2.5 py-2 text-xs text-foreground"
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                          >
                            <p className="font-medium">
                              {copy.replacesExisting}
                            </p>
                            <select
                              aria-label={copy.endPreviousRecurrence.replace(
                                "{date}",
                                new Intl.DateTimeFormat(
                                  language === "cs" ? "cs-CZ" : "en-GB",
                                ).format(transactionDate),
                              )}
                              value={
                                replacementTransactionIds[
                                  transaction.importId
                                ] ?? ""
                              }
                              onChange={(event) =>
                                setReplacementTransaction(
                                  transaction.importId,
                                  event.target.value
                                    ? Number(event.target.value)
                                    : null,
                                )
                              }
                              className="mt-2 h-8 w-full rounded-md border border-softiq-secondary/30 bg-background px-2 text-xs text-foreground outline-none focus:ring-2 focus:ring-ring"
                            >
                              <option value="">
                                {copy.keepPreviousRecurrence}
                              </option>
                              {recurringCandidates.map((candidate) => (
                                <option key={candidate.id} value={candidate.id}>
                                  {copy.endPreviousRecurrence.replace(
                                    "{date}",
                                    new Intl.DateTimeFormat(
                                      language === "cs" ? "cs-CZ" : "en-GB",
                                    ).format(transactionDate),
                                  )}
                                  {` · ${candidate.name} · ${new Intl.NumberFormat(
                                    language === "cs" ? "cs-CZ" : "en-GB",
                                    {
                                      style: "currency",
                                      currency: transaction.currency || "CZK",
                                    },
                                  ).format(candidate.amount)}`}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}

                {availableTransactions.length === 0 && (
                  <p className="text-sm text-foreground">
                    {copy.noNewTransactions}
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t border-border/70 px-6 py-4 sm:justify-end">
            {result &&
              (availableTransactions.length > 0 ||
                importVerifications.length > 0) && (
                <Button
                  type="button"
                  disabled={
                    selectedIds.size === 0 && importVerifications.length === 0
                  }
                  onClick={() => void addSelected()}
                >
                  {selectedIds.size > 0 ? copy.addSelected : copy.confirmImport}
                </Button>
              )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
